import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, Home } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, functions } from '../firebase';

function SubmissionThankYou() {
  const navigate = useNavigate();
  const location = useLocation();
  const submissionType = location.state?.submissionType || 'upload';
  const submissionDetails = location.state?.submissionDetails || {};
  const returnState = location.state?.returnState || null;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const labelMap = {
    lesson: 'Your lesson has been submitted.',
    worksheet: 'Your worksheet has been uploaded.',
    upload: 'Your upload has been submitted.'
  };

  const handleReturnHome = () => {
    if (returnState) {
      navigate('/Old_Home', { state: returnState });
    } else {
      navigate('/Old_Home');
    }
  };

  const formatSubmissionDetails = (details) => {
    const rows = Object.entries(details)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, char => char.toUpperCase());
        const formattedValue = Array.isArray(value) ? value.join(', ') : value;
        return `${formattedKey}: ${formattedValue}`;
      });

    return rows.length ? rows.join('\n') : 'No additional submission details were available.';
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const user = auth.currentUser;
      let profileDetails = {};

      if (user?.uid) {
        const profileSnap = await getDoc(doc(db, 'profiles', user.uid));
        if (profileSnap.exists()) {
          profileDetails = profileSnap.data();
        }
      }

      const submitterName =
        profileDetails.name ||
        profileDetails.displayName ||
        user?.displayName ||
        'Eascode user';

      const sendEmail = httpsCallable(functions, 'sendWorksheetEmail');

      await sendEmail({
        friendName: 'Eascode Team',
        friendEmail: 'eascode.ltd@gmail.com',
        worksheetTitle: `New ${submissionType} submission for review`,
        fileUrl: submissionDetails.fileUrl || 'https://eascode.web.app/Old_Home',
        message: `
New ${submissionType} submission has been confirmed for review.

Submitter:
Name: ${submitterName}
Email: ${user?.email || 'Not available'}
UID: ${user?.uid || 'Not available'}
City: ${profileDetails.city || 'Not available'}
School: ${profileDetails.school || 'Not available'}
Standard: ${profileDetails.standard || 'Not available'}

Submission details:
${formatSubmissionDetails(submissionDetails)}
        `.trim()
      });

      handleReturnHome();
    } catch (err) {
      console.error('Submission email failed:', err);
      setError('We could not send the notification email. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] px-4 py-8 flex items-center justify-center font-sans">
      <div className="w-full max-w-md bg-white border border-gray-100 rounded-lg shadow-sm p-6 sm:p-8 text-center">
        <div className="mx-auto mb-5 h-24 w-24 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
          <img
            src="/Eascode_No_Background.png"
            alt="Eascode"
            className="h-20 w-20 object-contain"
          />
        </div>

        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-50 text-green-600 flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7" />
        </div>

        <p className="text-sm font-bold text-blue-600 mb-2">
          {labelMap[submissionType] || labelMap.upload}
        </p>

        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3">
          Thanks for your submission
        </h1>

        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
          We will review your upload and get back to you if we have any questions or find an issue.
        </p>

        <p className="mt-5 text-sm font-semibold text-gray-800">
          We will respond within 5 working days to your submitted request.
        </p>

        {error && (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
            {error}
          </p>
        )}

        <div className="mt-7 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={handleReturnHome}
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-blue-400"
          >
            <Home className="h-4 w-4" />
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubmissionThankYou;
